"use client";

import { useState, useMemo, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import Navbar from "../../../../components/navbar";
import Footer from "../../../../components/footer";
import Button from "../../../../components/Button";
import styles from "../css/CompleteSplitProfile.module.css";
import { splitProfile } from "../../../../api/profile/split_profile";

export default function CompleteSplitProfile() {
  const navigate = useNavigate();
  const location = useLocation();

  const patientId = location.state?.patientId;
  const basePatientId = location.state?.basePatientId;
  const initialStayIds = location.state?.selectedStayIds || [];

  const stayIds = useMemo(() => initialStayIds, [initialStayIds]);

  const [showConfirm, setShowConfirm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [error, setError] = useState("");

  const canProceed = patientId && basePatientId;

  const profileData = location.state?.profileData || null;

  const mainProfile = useMemo(() => {
    if (!profileData) return null;
    return {
      profileNumber: profileData.profileNumber || "",
      fullName: profileData.fullName || "",
      idNumber: profileData.idNumber || "",
      emailAddress: profileData.emailAddress || "",
      sex: profileData.sex || "",
      homeCellNumber: profileData.homeCellNumber || "",
      chronicConditions: profileData.chronicConditions || "",
      medicalAidProvider: profileData.medicalAidProvider || "",
      medicalAidNumber: profileData.medicalAidNumber || "",
      doctor: profileData.doctor || "",
      practiceNumber: profileData.practiceNumber || "",
      postalAddress: [
        profileData.postalAddressLine1 || "",
        profileData.postalAddressLine2 || "",
        profileData.postalAddressLine3 || "",
        profileData.postalAddressLine4 || "",
      ],
      deliveryAddress: [
        profileData.deliveryAddressLine1 || "",
        profileData.deliveryAddressLine2 || "",
        profileData.deliveryAddressLine3 || "",
        profileData.deliveryAddressLine4 || "",
      ],
    };
  }, [profileData]);

  const beneficiaries = useMemo(() => {
    const bens = location.state?.beneficiaries || [];
    return bens;
  }, [location.state]);

  const allBeneficiaryIds = useMemo(
    () => beneficiaries.map((b) => b.id),
    [beneficiaries]
  );

  const moveCandidateIds = useMemo(
    () => allBeneficiaryIds.filter((id) => !stayIds.includes(id)),
    [allBeneficiaryIds, stayIds]
  );

  const moveCandidates = useMemo(
    () => beneficiaries.filter((b) => moveCandidateIds.includes(b.id)),
    [beneficiaries, moveCandidateIds]
  );

  const [selectedNewMainId, setSelectedNewMainId] = useState(null);

  useEffect(() => {
    if (!selectedNewMainId && moveCandidateIds.length > 0) {
      setSelectedNewMainId(moveCandidateIds[0]);
    }
  }, [moveCandidateIds, selectedNewMainId]);

  const selectedNewMain = useMemo(
    () => moveCandidates.find((b) => b.id === selectedNewMainId) || null,
    [moveCandidates, selectedNewMainId]
  );

  const originalProfileWithStayList = useMemo(() => {
    if (!mainProfile) return null;
    const stayNames = beneficiaries
      .filter((b) => stayIds.includes(b.id))
      .map((b) => b.name);
    return { ...mainProfile, stayBeneficiaries: stayNames };
  }, [mainProfile, beneficiaries, stayIds]);

  const newProfile = useMemo(() => {
    if (!mainProfile || !selectedNewMain) return null;
    return {
      profileNumber: "",
      fullName: selectedNewMain.name || "",
      idNumber: selectedNewMain.idNumber || "",
      emailAddress: selectedNewMain.emailAddress || "",
      sex: selectedNewMain.sex || "",
      homeCellNumber: selectedNewMain.homeCellNumber || "",
      chronicConditions: selectedNewMain.chronicConditions || "",

      medicalAidProvider: mainProfile.medicalAidProvider,
      medicalAidNumber: mainProfile.medicalAidNumber,
      doctor: mainProfile.doctor,
      practiceNumber: mainProfile.practiceNumber,
      postalAddress: mainProfile.postalAddress,
      deliveryAddress: mainProfile.deliveryAddress,
    };
  }, [mainProfile, selectedNewMain]);

  const handleSplit = () => {
    if (!canProceed || !originalProfileWithStayList || !newProfile || moveCandidateIds.length === 0) {
      return;
    }
    setShowConfirm(true);
  };

  const handleConfirmSplit = async () => {
    if (!canProceed || !selectedNewMainId || moveCandidateIds.length === 0) {
      return;
    }

    const newMainPatientId = Number(selectedNewMainId);
    const stayBeneficiaryIds = stayIds;
    const moveBeneficiaryIds = allBeneficiaryIds.filter((id) => !stayBeneficiaryIds.includes(id));

    try {
      setIsSubmitting(true);
      setError("");
      await splitProfile({
        basePatientId,
        newMainPatientId,
        stayBeneficiaryIds,
        moveBeneficiaryIds,
      });
      setShowConfirm(false);
      setShowSuccess(true);
    } catch (err) {
      setIsSubmitting(false);
      setError("Failed to split profile. Please try again.");
      setShowConfirm(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDone = () => {
    setShowSuccess(false);
    navigate("/customer/profiles/split-profiles");
  };

  const renderProfileForm = (profile, isFirst) => (
    <div className={styles.formContainer}>
      <h3 className={styles.mainTitle}>
        {isFirst ? `Profile Number: ${profile.profileNumber}` : "New Profile"}
      </h3>
      <div className={styles.layout}>
        <div className={styles.leftColumn}>
          <div className={styles.fieldRow}>
            <div className={styles.field}>
              <label>Full Name</label>
              <input type="text" value={profile.fullName} readOnly />
            </div>
            <div className={styles.field}>
              <label>ID Number</label>
              <input type="text" value={profile.idNumber} readOnly />
            </div>
          </div>

          <div className={styles.fieldRow}>
            <div className={styles.field}>
              <label>Email Address</label>
              <input type="text" value={profile.emailAddress} readOnly />
            </div>
            <div className={styles.field}>
              <label>Sex</label>
              <input type="text" value={profile.sex} readOnly />
            </div>
          </div>

          <div className={styles.fieldRow}>
            <div className={styles.field}>
              <label>Home/Cell Number</label>
              <input type="text" value={profile.homeCellNumber} readOnly />
            </div>
            <div className={styles.field}>
              <label>Chronic Conditions</label>
              <input type="text" value={profile.chronicConditions} readOnly />
            </div>
          </div>

          <div className={styles.fieldRow}>
            <div className={styles.field}>
              <label>Medical Aid Provider</label>
              <input type="text" value={profile.medicalAidProvider} readOnly />
            </div>
            <div className={styles.field}>
              <label>Medical Aid Number</label>
              <input type="text" value={profile.medicalAidNumber} readOnly />
            </div>
          </div>

          <div className={styles.fieldRow}>
            <div className={styles.field}>
              <label>Doctor</label>
              <input type="text" value={profile.doctor} readOnly />
            </div>
            <div className={styles.field}>
              <label>Practice Number</label>
              <input type="text" value={profile.practiceNumber} readOnly />
            </div>
          </div>

          {!isFirst && (
            <div className={styles.radioGroup}>
              <label>Select Main Member:</label>
              <select
                value={selectedNewMainId || ""}
                onChange={(e) => setSelectedNewMainId(Number(e.target.value) || null)}
              >
                {moveCandidates.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className={styles.rightColumn}>
          <div className={styles.addressGroup}>
            <label>Postal Address</label>
            {profile.postalAddress.map((line, index) => (
              <input key={index} type="text" value={line} readOnly />
            ))}
          </div>
          <div className={styles.addressGroup}>
            <label>Delivery Address</label>
            {profile.deliveryAddress.map((line, index) => (
              <input key={index} type="text" value={line} readOnly />
            ))}
          </div>
          {isFirst && profile.stayBeneficiaries && profile.stayBeneficiaries.length > 0 && (
            <div className={styles.beneficiarySection}>
              <div className={styles.beneficiarySectionTitle}>
                Beneficiaries Staying with Original Main Member
              </div>
              <div className={styles.beneficiaryChips}>
                {profile.stayBeneficiaries.map((name) => (
                  <span key={name} className={styles.beneficiaryChip}>
                    {name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className={styles.app}>
      <div className={styles.mainContainer}>
        <Navbar />
        <div className={styles.content}>
          <h2 className={styles.pageTitle}>Split Profile</h2>
          {originalProfileWithStayList && newProfile && (
            <div className={styles.formsRow}>
              {renderProfileForm(originalProfileWithStayList, true)}
              {renderProfileForm(newProfile, false)}
            </div>
          )}
          <div className={styles.buttonContainer}>
            <Button variant="primary" onClick={handleSplit}>
              Confirm Split
            </Button>
          </div>
        </div>
        <div className={styles.footerWrapper}>
          <Footer />
        </div>
      </div>

      {showConfirm && (
        <div className={styles.confirmOverlay}>
          <div className={styles.confirmPopup}>
            <div className={styles.popupHeader}>
              <span className={styles.exclamationIcon} style={{ padding: '0.25rem 0.5rem' }}>!</span>
              <h3 className={styles.popupTitle}>Are you sure you want to split this profile?</h3>
            </div>
            <div className={styles.popupButtons}>
              <Button variant="primary" onClick={handleConfirmSplit}>
                Yes
              </Button>
              <Button variant="secondary" onClick={() => setShowConfirm(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {showSuccess && (
        <div className={styles.successOverlay}>
          <div className={styles.successPopup}>
            <div className={styles.successIcon}>✓</div>
            <h3 className={styles.successTitle}>Profiles Split Successfully</h3>
            <div className={styles.successButtons}>
              <Button variant="primary" onClick={handleDone}>
                Done
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}